import { Route } from '@angular/router';
import { initialDataResolver } from './app.resolvers';
import { AuthGuard } from './core/auth/guards/auth.guard';
import { NoAuthGuard } from './core/auth/guards/noAuth.guard';
import { LayoutComponent } from './layout/layout.component';
import { PermisoGuard } from '@core/auth/guards/permiso.guard';
import { ReporteComponent } from './shared/components/reportes/reporte.component';
import { CalculadoraCobrosComponent } from './modules/ventas/cobranzas/components/calculadora-de-cobros/calculadora-cobros.component';
import { EmbedRedirectComponent } from './modules/embed/embed-redirect.component';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path to '/example'
    { path: '', pathMatch: 'full', redirectTo: 'example' },

    // Redirect 'denegado' path to '/acceso-denegado'
    { path: 'acceso-denegado', pathMatch: 'full', redirectTo: 'acceso-denegado' },

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'example' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'confirmation-required', loadChildren: () => import('./modules/auth/confirmation-required/confirmation-required.routes') },
            { path: 'forgot-password', loadChildren: () => import('./modules/auth/forgot-password/forgot-password.routes') },
            { path: 'reset-password', loadChildren: () => import('./modules/auth/reset-password/reset-password.routes') },
            { path: 'sign-in', loadChildren: () => import('./modules/auth/sign-in/sign-in.routes') },
            { path: 'sign-up', loadChildren: () => import('./modules/auth/sign-up/sign-up.routes') }
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            { path: 'sign-out', loadChildren: () => import('./modules/auth/sign-out/sign-out.routes') },
            { path: 'change-password', loadChildren: () => import('./modules/auth/change-password/change-password.routes') },
            { path: 'acceso-denegado', loadChildren: () => import('./modules/admin/acceso-denegado/acceso-denegado.routes') },
            {
                path: 'example',
                title: 'Inicio',
                loadChildren: () => import('./modules/admin/example/example.routes')
            },
            {
                path: 'emails',
                title: 'Mails Enviados / Webmail',
                loadChildren: () => import('./modules/emails/emails.routes')
            },
            {
                path: 'prototipos',
                title: 'Prototipos',
                loadChildren: () => import('./prototipos/prototipos.routes')
            },
            {
                matcher: (url) => {
                    if (url.length > 0 && url[0].path === 'embed') {
                        return { consumed: url };
                    }
                    return null;
                },
                component: EmbedRedirectComponent,
                data: {
                    layout: 'empty'
                }
            },
            // ==== Administracion ====
            {
                path: 'bancos',
                title: 'Bancos / Administración',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/administracion/bancos/bancos.module').then(m => m.BancosModule)
            },
            {
                path: 'clientes',
                title: 'Clientes / Ventas',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/administracion/clientes/clientes.module').then(m => m.ClientesModule)
            },
            {
                path: 'estados',
                title: 'Estados / Ventas',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/administracion/estados-ventas/estados-ventas.module').then(m => m.EstadosVentasModule)
            },
            {
                path: 'medios-de-cobro',
                title: 'Medios de cobro / Ventas',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/administracion/medios-de-cobro/medios-de-cobro.module').then(m => m.MediosDeCobroModule)
            },
            {
                path: 'medios-de-pago',
                title: 'Medios de pago / Compras',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/administracion/medios-de-pago/medios-de-pago.module').then(m => m.MediosDePagoModule)
            },

            {
                path: 'tratamientos',
                title: 'Tratamientos / Administración',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/administracion/tratamientos/tratamientos.module').then(m => m.TratamientosModule)
            },
            // ==== Tareas ====
            {
                path: 'gestion-tareas',
                title: 'Gestión de Tareas',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/compras/gestion-tareas/gestion-tareas.module').then(m => m.GestionTareasModule)
            },
            // ==== Compras ====
            {
                path: 'proveedores',
                title: 'Cuentas Corriente - Proveedores / Compras',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/administracion/proveedores/proveedores.module').then(m => m.ProveedoresModule)
            },
            {
                path: 'facturas-de-compras/:idtipoegreso',
                title: 'Gastos / Compras',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/compras/facturas-de-compra/facturas-de-compra.module').then(m => m.FacturasDeCompraModule)
            },
            {
                path: 'ordenes-de-compra',
                title: 'Ordenes de compra / Compras',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/compras/ordenes-de-compra/ordenes-de-compra.module').then(m => m.OrdenesDeCompraModule)
            },
            {
                path: 'ordenes-de-pago/:idtipopago',
                title: 'Ordenes de pago / Compras',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/compras/ordenes-de-pago/ordenes-de-pago.module').then(m => m.OrdenesDePagoModule)
            },
            {
                path: 'anticipos-proveedores',
                title: 'Órdenes de Pago & Anticipos / Compras',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/compras/anticipos-proveedores/anticipos-proveedores.module').then(m => m.AnticiposProveedoresModule)
            },
            {
                path: 'pedidos-de-presupuesto',
                title: 'Pedidos de presupuesto / Compras',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/compras/pedidos-de-presupuesto/pedidos-de-presupuestos.module').then(m => m.PedidosDePresupuestoModule)
            },
            {
                path: 'periodos-de-compras',
                title: 'Periodos de compras / Compras',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/compras/periodos-de-compras/periodos-de-compras.module').then(m => m.PeriodosDeComprasModule)
            },
            // ==== Fondos ====
            {
                path: 'fondos',
                title: 'Fondos / Fondos',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/fondos/fondos/fondos.module').then(m => m.FondosModule)
            },
            {
                path: 'movimientos',
                title: 'Movimientos / Fondos',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/fondos/movimientos/movimientos.module').then(m => m.MovimientosModule)
            },
            {
                path: 'tipo-de-movimientos',
                title: 'Tipo de movimientos / Fondos',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/fondos/tipo-movimientos/tipo-movimientos.module').then(m => m.TipoMovimientosModule)
            },
            {
                path: 'indicadores',
                title: 'Ventas vs Compras / Indicadores',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/indicadores/indicadores.module').then(m => m.indicadoresModule)
            },
            {
                path: 'indicadores-de-venta',
                title: 'Indicadores de venta / Indicadores',
                canActivate: [PermisoGuard],
                loadComponent: () => import('./modules/indicadores/components/indicadores-venta/indicadores-venta.component').then(m => m.IndicadoresVentaComponent)
            },
            {
                path: 'indicadores-centro-costo',
                title: 'Indicadores por Centros de Costo / Indicadores',
                loadComponent: () => import('./modules/indicadores/components/indicadores-centro-costo/indicadores-centro-costo.component').then(m => m.IndicadoresCentroCostoComponent)
            },
            {
                path: 'indicador-stock',
                title: 'Indicador de Stock',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/indicadores/components/dashboard-stock/dashboard-stock.module').then(m => m.DashboardStockModule)
            },
            {
                path: 'indicadores-produccion',
                title: 'Indicadores IVA / Indicadores',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/indicadores-produccion/indicadores-produccion.routes')
            },
            // ==== Seguridad ====
            {
                path: 'usuarios',
                title: 'Usuarios / Seguridad',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/seguridad/usuarios/usuarios.module').then(m => m.UsuariosModule)
            },
            {
                path: 'menues',
                title: 'Menúes / Seguridad',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/seguridad/menues/menues.module').then(m => m.MenuesModule)
            },
            {
                path: 'empresas',
                title: 'Empresas / Seguridad',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/seguridad/empresas/empresas.module').then(m => m.EmpresasModule)
            },
            {
                path: 'items',
                title: 'Items / Seguridad',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/seguridad/items/items.module').then(m => m.ItemsModule)
            },
            {
                path: 'roles',
                title: 'Roles / Seguridad',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/seguridad/roles/roles.module').then(m => m.RolesModule)
            },
            // ==== Stock ====
            {
                path: 'articulos',
                title: 'Artículos / Gestión Comercial',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/articulos/articulos.module').then(m => m.ArticulosModule)
            },
            {
                path: 'insumos',
                title: 'Insumos / Gestión Comercial',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/insumos/insumos.module').then(m => m.InsumosModule)
            },
            {
                path: 'productos',
                title: 'Productos / Gestión Comercial',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/productos/producto.module').then(m => m.ProductosModule)
            },
            {
                path: 'servicios',
                title: 'Servicios / Gestión Comercial',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/servicios/servicios.module').then(m => m.ServiciosModule)
            },
            {
                path: 'costos',
                title: 'Costos / Gestión Comercial',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/costos/costos.module').then(m => m.CostosModule)
            },
            {
                path: 'fabricados',
                title: 'Fabricados / Gestión Comercial',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/fabricados/fabricados.module').then(m => m.FabricadosModule)
            },
            {
                path: 'depositos',
                title: 'Depósitos / Stock',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/depositos/depositos.module').then(m => m.DepositosModule)
            },
            {
                path: 'devoluciones',
                title: 'Devoluciones / Stock',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/devoluciones/devoluciones.module').then(m => m.DevolucionesModule)
            },
            {
                path: 'devoluciones-externas',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/devoluciones-externas/devoluciones-externas.module').then(m => m.DevolucionesExternasModule)
            },
            {
                path: 'entrega_qr',
                title: 'Entrega QR / Stock',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/entregas-qr/entregas-qr.module').then(m => m.EntregasQRModule)
            },
            {
                path: 'entregas',
                title: 'Entregas / Stock',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/entregas/entregas.module').then(m => m.EntregasModule)
            },
            {
                path: 'entregas-externas',
                title: 'Entregas externas / Stock',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/entregas-externas/entregas-externas.module').then(m => m.EntregasExternasModule)
            },
            {
                path: 'entregas-rapidas',
                title: 'Pañol / Stock',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/entregas-rapidas/entregas-rapidas.routes')
            },
            {
                path: 'movimiento-conversion',
                title: 'Movimiento conversión / Stock',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/movimiento-conversion/movimiento-conversion.module').then(m => m.MovimientoConversionModule)
            },
            {
                path: 'movimientos-internos',
                title: 'Movimientos internos / Stock',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/movimientos-internos/movimientos-internos.module').then(m => m.MovimientosInternosModule)
            },
            {
                path: 'remitos',
                title: 'Remitos / Stock',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/remitos/remitos.module').then(m => m.RemitosModule)
            },
            {
                path: 'rubros',
                title: 'Rubros / Stock',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/rubros/rubros.module').then(m => m.RubrosModule)
            },
            {
                path: 'stock-inicial',
                title: 'Stock Inicial / Stock',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/stock-inicial/stock-inicial.module').then(m => m.StockInicialModule)
            },
            {
                path: 'inventario',
                title: 'Inventario / Stock',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/inventario/inventario.module').then(m => m.InventarioModule)
            },
            {
                path: 'unidades-de-medida',
                title: 'Unidades de medida / Stock',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/stock/unidades-de-medida/unidades-de-medida.module').then(m => m.UnidadesDeMedidaModule)
            },
            // Internos
            {
                path: 'internos',
                title: 'Internos',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/internos/internos.module').then(m => m.InternosModule)
            },
            // Ventas
            {
                path: 'calculadora-de-cobros',
                title: 'Calculadora de cobros / Ventas',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/ventas/cobranzas/components/calculadora-de-cobros/calculadora-cobros.module').then(m => m.CalculadoraCobrosModule)
            },
            {
                path: 'cobranzas',
                title: 'Cobranzas / Ventas',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/ventas/cobranzas/cobranzas.module').then(m => m.CobranzasModule)
            },
            {
                path: 'cobranzas-anticipos',
                title: 'Cobranzas y Anticipos / Ventas',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/ventas/cobranzas-anticipos/anticipos-clientes.module').then(m => m.AnticiposClientesModule)
            },
            {
                path: 'facturas',
                title: 'Facturas / Ventas',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/ventas/facturacion/facturacion.module').then(m => m.FacturacionModule)
            },
            {
                path: 'pagares',
                title: 'Pagarés / Ventas',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/ventas/pagares/pagares.module').then(m => m.PagaresModule)
            },
            {
                path: 'ventas',
                title: 'Ventas / Ventas',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/ventas/genericas/venta-generica.module').then(m => m.VentaGenericasModule)
            },
            {
                path: 'presupuestos',
                title: 'Presupuestos / Ventas',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/ventas/presupuestos/presupuestos.module').then(m => m.PresupuestosModule)
            },
            {
                path: 'prospectos',
                title: 'Prospectos / Ventas',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/ventas/prospectos/prospectos.module').then(m => m.ProspectosModule)
            },
            // Configuracion
            {
                path: 'configuracion/:id',
                title: 'Configuración / Configuración',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/configuracion/configuracion.module').then(m => m.ConfiguracionModule)
            },
            // RRHH
            {
                path: 'rrhh',
                title: 'RRHH / RRHH',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/rrhh/rrhh.module').then(m => m.RRHHModule)
            },
            // Inmuebles
            {
                path: 'complejos',
                title: 'Complejos / Inmuebles',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/inmuebles/complejos/complejos.module').then(m => m.ComplejosModule)
            },
            {
                path: 'unidades-funcionales',
                title: 'Unidades funcionales / Inmuebles',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/inmuebles/unidades-funcionales/unidades-funcionales.module').then(m => m.UnidadesFuncionalesModule)
            },
            // Recordatorios
            {
                path: 'recordatorios',
                title: 'Recordatorios',
                loadChildren: () => import('./modules/recordatorios/recordatorios.module').then(m => m.RecordatoriosModule)
            },
            // Soporte
            {
                path: 'soporte',
                title: 'Soporte / Soporte',
                loadChildren: () => import('./modules/soporte/soporte.module').then(m => m.SoporteModule)
            },
            // Auxiliar
            {
                path: 'punto-de-venta',
                title: 'Punto de Venta',
                canActivate: [PermisoGuard],
                loadChildren: () => import('./modules/punto-de-venta/punto-de-venta.module').then(m => m.PuntoDeVentaModule)
            },
            {
                path: 'reporte/:path',
                component: ReporteComponent
            },
        ]
    }
    // Auth routes for authenticated users
    // {
    //     path: '',
    //     canActivate: [AuthGuard],
    //     canActivateChild: [AuthGuard],
    //     component: LayoutComponent,
    //     data: {
    //         layout: 'empty'
    //     },
    //     children: [
    //         { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
    //         { path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes') },
    //         {
    //             path: 'example',
    //             loadChildren: () => import('app/modules/admin/example/example.routes')
    //         },
    //         {
    //             path: 'bancos',
    //             loadChildren: () => import('app/modules/administracion/bancos/bancos.module').then(m => m.BancosModule)
    //         },
    //         {
    //             path: 'tratamientos',
    //             loadChildren: () => import('app/modules/administracion/tratamientos/tratamientos.module').then(m => m.TratamientosModule)
    //         },
    //         {
    //             path: 'proveedores',
    //             loadChildren: () => import('app/modules/administracion/proveedores/proveedores.module').then(m => m.ProveedoresModule)
    //         },
    //         {
    //             path: 'fondos',
    //             loadChildren: () => import('app/modules/fondos/fondos/fondos.module').then(m => m.FondosModule)
    //         },
    //         {
    //             path: 'tipo-de-movimientos',
    //             loadChildren: () => import('app/modules/fondos/tipo-movimientos/tipo-movimientos.module').then(m => m.TipoMovimientosModule)
    //         },
    //         {
    //             path: 'movimientos',
    //             loadChildren: () => import('app/modules/fondos/movimientos/movimientos.module').then(m => m.MovimientosModule)
    //         },
    //         {
    //             path: 'medios-de-cobro',
    //             loadChildren: () => import('app/modules/administracion/medios-de-cobro/medios-de-cobro.module').then(m => m.MediosDeCobroModule)
    //         },
    //         {
    //             path: 'unidades-de-medida',
    //             loadChildren: () => import('app/modules/stock/unidades-de-medida/unidades-de-medida.module').then(m => m.UnidadesDeMedidaModule)
    //         },
    //         {
    //             path: 'depositos',
    //             loadChildren: () => import('app/modules/stock/depositos/depositos.module').then(m => m.DepositosModule)
    //         },
    //         {
    //             path: 'rubros',
    //             loadChildren: () => import('app/modules/stock/rubros/rubros.module').then(m => m.RubrosModule)
    //         },
    //         {
    //             path: 'articulos',
    //             loadChildren: () => import('app/modules/stock/articulos/articulos.module').then(m => m.ArticulosModule)
    //         },
    //     ]
    // },



    // // Landing routes
    // {
    //     path: '',
    //     component: LayoutComponent,
    //     data: {
    //         layout: 'empty'
    //     },
    //     children: [
    //         { path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes') },
    //     ]
    // },


];
